import React, { useEffect, useRef, useState } from 'react';
import { Loader2, MapPin } from 'lucide-react';

import { suggestAddress, validateAddress } from '../../../services/address';

// Add a VERIFIED address right from the checklist (Nic 2026-09-28): type,
// pick a suggestion, and it is verified (Smarty geocode) and saved, the same
// rule as every other address in the app. Apt / Suite is optional.
const AddressAdder = ({ disabled, saving, onSave }) => {
  const [query, setQuery] = useState('');
  const [line2, setLine2] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [picked, setPicked] = useState(null);
  const rootRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (picked) return undefined;
    const q = query.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return undefined;
    }
    const t = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setBusy(true);
      setErr('');
      try {
        const out = await suggestAddress(q, { signal: controller.signal });
        setSuggestions(out);
        setOpen(out.length > 0);
      } catch (e) {
        if (e?.name !== 'CanceledError' && e?.code !== 'ERR_CANCELED') {
          setErr('Address lookup is unavailable right now. Add it on their profile instead.');
        }
      } finally {
        setBusy(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, picked]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const pick = async (s) => {
    setOpen(false);
    setQuery([s.line1, s.city, s.state].filter(Boolean).join(', '));
    setBusy(true);
    setErr('');
    try {
      let full = s;
      if (s.latitude == null || s.longitude == null) {
        const res = await validateAddress({
          line1: s.line1,
          line2: null,
          city: s.city,
          state: s.state,
          zip: s.zip,
          country: s.country || 'US',
        });
        if (!res?.ok || !res.suggestion) {
          setErr("That address couldn't be verified. Try another suggestion.");
          setPicked(null);
          return;
        }
        full = { ...s, ...res.suggestion };
      }
      setPicked(full);
    } catch (e) {
      setErr("That address couldn't be verified. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={rootRef} className="mt-3 space-y-2">
      <div>
        {/* The pin and the spinner center on the INPUT, not on the box that
            also holds the suggestions (QA 2026-11-03: once the list opened,
            halfway down the box was the first suggestion). */}
        <div className="relative">
        <MapPin size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={query}
          disabled={disabled}
          onChange={(e) => {
            setQuery(e.target.value);
            setPicked(null);
          }}
          onFocus={() => suggestions.length && setOpen(true)}
          placeholder="Start typing their street address"
          className="h-9 w-full rounded-lg border-2 border-slate-200 bg-white pl-8 pr-8 text-[13px] text-slate-700 outline-none focus:border-indigo-400 disabled:opacity-60 dark:border-white/10 dark:!bg-white/5 dark:text-slate-100"
        />
        {busy ? (
          <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
        ) : null}
        </div>
        {open ? (
          // In the flow, not floating: the step card clips overflow.
          <div className="mt-1 max-h-60 overflow-y-auto rounded-xl bg-white py-1 shadow-[0_4px_16px_rgba(15,23,42,0.10)] ring-1 ring-slate-200/80 dark:!bg-surface-elevated dark:ring-white/10">
            {suggestions.map((s, i) => (
              <button
                key={`${s.line1}-${s.zip}-${i}`}
                type="button"
                onClick={() => pick(s)}
                className="block w-full px-3 py-2 text-left text-[13px] outline-none hover:bg-slate-50 focus:outline-none dark:hover:!bg-white/10"
              >
                <div className="font-medium text-slate-700 dark:text-slate-100">{s.line1}</div>
                <div className="text-xs text-slate-400">
                  {[s.city, s.state].filter(Boolean).join(', ')} {s.zip}
                </div>
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {picked ? (
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={line2}
            onChange={(e) => setLine2(e.target.value)}
            placeholder="Apt, suite (optional)"
            className="h-9 w-40 rounded-lg border-2 border-slate-200 bg-white px-3 text-[13px] text-slate-700 outline-none focus:border-indigo-400 dark:border-white/10 dark:!bg-white/5 dark:text-slate-100"
          />
          <button
            type="button"
            disabled={saving || disabled}
            onClick={() => onSave({ ...picked, line2 })}
            className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg bg-emerald-600 px-4 text-[13px] font-semibold text-white outline-none hover:bg-emerald-500 focus:outline-none disabled:opacity-50"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : null}
            Save address
          </button>
        </div>
      ) : null}
      {err ? <div className="text-xs font-semibold text-rose-600 dark:text-rose-300">{err}</div> : null}
    </div>
  );
};

export default AddressAdder;
